import { cn } from '~/utils'
import { Badge } from '~/elements'
import { ComponentProps, ReactNode } from 'react'
import { BarList } from '@tremor/react'
import { Popover, PopoverTrigger, PopoverContent } from '@bpinternal/ui-kit-next'
import { segregateUsages } from '../utils'

type Props = {
  className?: string
  breakdowns: {
    id: string
    value: number
    name?: string
  }[]
  topN: number
  valueFormatter?: (value: number) => string
}

export const BreakdownLegend = ({ className, breakdowns, valueFormatter, topN, ...props }: Props) => {
  const { usages, remainder } = segregateUsages(breakdowns, topN)

  const otherUsagesData = breakdowns.slice(topN).map(({ value, name, id }) => ({ value, name: name ?? id }))

  return (
    <div {...props} className={cn('flex w-full min-w-0 flex-col gap-3', className)}>
      {usages.map(({ name, value }, i) => (
        <UsageLegendItem
          key={`${name}-${i}`}
          chartColor={(i + 1) as 1 | 2 | 3 | 4 | 5}
          className="w-full"
          label={name}
          title={name}
          badge={valueFormatter ? valueFormatter(value) : `${value}`}
        />
      ))}

      {remainder !== undefined && (
        <>
          <UsageLegendItem
            chartColor={(topN + 1) as 1 | 2 | 3 | 4 | 5}
            label={
              <Popover>
                <PopoverTrigger asChild>
                  <button className="cursor-pointer text-blue-700">Other</button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-64 overflow-y-auto"
                  style={{ maxHeight: 'var(--radix-popover-content-available-height)' }}
                  collisionPadding={30}
                  align="start"
                >
                  <BarList valueFormatter={valueFormatter} data={otherUsagesData} color="blue" />
                </PopoverContent>
              </Popover>
            }
            badge={valueFormatter ? valueFormatter(remainder) : `${remainder}`}
          />
        </>
      )}
    </div>
  )
}

type UsageLegendItemProps = {
  chartColor?: 1 | 2 | 3 | 4 | 5
  label?: ReactNode
  badge?: string
} & ComponentProps<'div'>

const chartBackgroundColors = {
  1: 'bg-chart-1',
  2: 'bg-chart-2',
  3: 'bg-chart-3',
  4: 'bg-chart-4',
  5: 'bg-chart-5',
}

const UsageLegendItem = ({ chartColor = 1, label, badge, className, title, ...props }: UsageLegendItemProps) => {
  return (
    <div className={cn('flex items-center gap-1', className)} {...props}>
      <div className={`${chartBackgroundColors[chartColor]} size-2 flex-none rounded-sm`} />
      <span className="truncate text-sm text-neutral-900" title={title}>
        {label}
      </span>
      <Badge variant="secondary" className="ml-auto w-fit justify-self-center font-medium text-neutral-900">
        {badge}
      </Badge>
    </div>
  )
}
